import { useMemo } from 'react';
import { Blip } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-quality/v1/api.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { CallQualityFeedback, MetricSection } from './call-quality-components';
import { MosChartSection, OverviewSection } from './call-quality-drilldown-components';
import { calculateQuality } from './call-quality-drilldown-screen-old';
import { createMetricData, formatDuration } from './call-quality-utils';
import { MetricData } from './types';

type BaseProps = {
  data: { label: string; blip: Blip; startTime: string; channelId: string; locationId: string };
};

const CallQualityDrilldownScreenHeader = ({ data, onBack }: BaseProps & { onBack: () => void }) => {
  const { t } = useTranslation('calls');

  return (
    <div
      style={{
        display: 'grid',
        alignItems: 'center',
        columnGap: theme.spacing(1),
        gridTemplateColumns: 'auto 1fr',
        gridTemplateAreas: '"back title" ". label"',
      }}
    >
      <Button iconName='caret-left' variant='secondary' css={{ gridArea: 'back' }} onClick={onBack} />
      <Heading level={2} css={{ margin: 0, gridArea: 'title' }}>
        {t('Low Quality Interval')}
      </Heading>
      <Text css={{ gridArea: 'label' }} as='span' size='medium'>
        {data.label}
      </Text>
    </div>
  );
};

const CallQualityDrilldownScreenContent = ({ data }: BaseProps) => {
  const { t } = useTranslation('calls');
  const { blip } = data;

  const metricData: MetricData[] = useMemo(
    () => [
      {
        type: 'mosAvg',
        values: [createMetricData({ type: 'mos', value: blip.avgMosScore })],
      },
      {
        type: 'packetLossAvg',
        values: [
          createMetricData({
            type: 'packetLoss',
            value: blip.avgPacketLossPercentage.inbound ?? 0,
            direction: 'inbound',
          }),
          createMetricData({
            type: 'packetLoss',
            value: blip.avgPacketLossPercentage.outbound ?? 0,
            direction: 'outbound',
          }),
        ],
      },
      {
        type: 'jitterAvg',
        values: [
          createMetricData({
            type: 'jitter',
            value: blip.avgJitterInMillisecond.inbound ?? 0,
            direction: 'inbound',
          }),
          createMetricData({
            type: 'jitter',
            value: blip.avgJitterInMillisecond.outbound ?? 0,
            direction: 'outbound',
          }),
        ],
      },
    ],
    [blip]
  );

  const quality = calculateQuality(blip);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'repeat(auto-fit, auto)',
          padding: theme.spacing(3),
          gap: theme.spacing(2),
        }}
      >
        <OverviewSection
          subtitle={data.label}
          quality={quality}
          mos={blip.avgMosScore.toFixed(1)}
          duration={formatDuration(blip.durationInSeconds * 1000)}
          eventCount={blip.blipEvents.length}
        />
        <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
        <MetricSection
          subtitle={
            <div>
              <Text size='medium' color='light'>
                {data.label}
              </Text>
              <Text size='medium' color='light'>
                {t('Averages are calculated from the events in this low-quality interval.')}
              </Text>
            </div>
          }
          data={metricData}
        />
        <div css={{ borderTop: `1px solid ${theme.colors.neutral20}` }} />
        <MosChartSection subtitle={data.label} blipEvents={blip.blipEvents} />
        <CallQualityFeedback startTime={data.startTime} channelId={data.channelId} locationId={data.locationId} />
      </div>
    </div>
  );
};

export const CallQualityDrilldownScreen = {
  Header: CallQualityDrilldownScreenHeader,
  Content: CallQualityDrilldownScreenContent,
};
